/**
 * useDecisionRelatedNames — the name of a thing a decision is on the record
 * with (an item, a session, a cycle, a track, an item pool).
 *
 * One lookup for every surface that shows a decision's related objects: the
 * decision log's pills and the decision's own page. A reader is never shown an
 * identifier where a name belongs, so an object whose name is not loaded yet
 * reads as its kind ("Session"), not as the first eight characters of its id.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { DECISION_RELATED_OBJECT_KIND_LABELS } from '@/types/prioritization/decisions';

/** `related_objects` is written by the database too, so the kinds are open. */
const EXTRA_KIND_LABELS: Record<string, string> = {
  item_pool: 'Item pool',
  // The table is priority_sets; people read "priority list" (owner ruling 2026-10-15).
  priority_set: 'Priority list',
};

/** A plain word for a related object's kind — never the raw column value. */
export function decisionRelatedKindLabel(kind: string): string {
  // Stored data is not always the shape the type promises.
  if (typeof kind !== 'string' || !kind) return 'Related';
  return (
    (DECISION_RELATED_OBJECT_KIND_LABELS as Record<string, string>)[kind] ??
    EXTRA_KIND_LABELS[kind] ??
    kind.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
  );
}

export interface DecisionRelatedNames {
  /** The object's own name, or null when it is not loaded or has none. */
  nameFor: (ref: { kind: string; id: string }) => string | null;
}

/**
 * @param wanted the related objects about to be shown. Any whose kind has
 *   nothing loaded yet triggers that kind's one fetch, so a page opened
 *   directly by its URL still gets names.
 */
export function useDecisionRelatedNames(
  wanted: readonly { kind: string; id: string }[] = [],
): DecisionRelatedNames {
  const items = useItemsStore((s) => s.items);
  const sessions = useSessionStore((s) => s.sessions);
  const cycles = useDegapProcessStore((s) => s.cycles);
  const tracks = useTrackStore((s) => s.tracks);
  const itemPools = useItemPoolsStore((s) => s.itemPools);

  const maps = useMemo(() => {
    const byKind: Record<string, Map<string, string>> = {
      item: new Map(items.map((i) => [i.id, i.title])),
      session: new Map(sessions.map((s) => [s.id, s.name])),
      cycle: new Map(cycles.map((c) => [c.id, c.name])),
      track: new Map(tracks.map((t) => [t.id, t.name])),
      item_pool: new Map(itemPools.map((p) => [p.id, p.name])),
    };
    return byKind;
  }, [items, sessions, cycles, tracks, itemPools]);

  const nameFor = useCallback(
    (ref: { kind: string; id: string }) => {
      const name = maps[ref.kind]?.get(ref.id);
      return name && name.trim() ? name : null;
    },
    [maps],
  );

  // The kinds that are wanted and still unnamed, as a stable string so the
  // effect below runs once per change rather than once per render.
  const missingKinds = useMemo(() => {
    const kinds = new Set<string>();
    for (const ref of wanted) {
      if (maps[ref.kind] && !maps[ref.kind].has(ref.id)) kinds.add(ref.kind);
    }
    return [...kinds].sort().join(',');
  }, [wanted, maps]);

  useEffect(() => {
    if (!missingKinds) return;
    for (const kind of missingKinds.split(',')) {
      // Each store is asked at most once per mount for a kind it has nothing
      // for; an id that is still unnamed after that read is simply not there.
      if (kind === 'item' && useItemsStore.getState().items.length === 0) {
        void useItemsStore.getState().fetchFromSupabase();
      } else if (kind === 'session' && useSessionStore.getState().sessions.length === 0) {
        void useSessionStore.getState().fetchFromSupabase();
      } else if (kind === 'cycle' && useDegapProcessStore.getState().cycles.length === 0) {
        void useDegapProcessStore.getState().fetchFromSupabase();
      } else if (kind === 'track' && useTrackStore.getState().tracks.length === 0) {
        void useTrackStore.getState().fetchAllTracks();
      } else if (kind === 'item_pool' && useItemPoolsStore.getState().itemPools.length === 0) {
        void useItemPoolsStore.getState().fetchFromSupabase();
      }
    }
  }, [missingKinds]);

  return { nameFor };
}
